// src/AppLayout.tsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Layout, Menu, Dropdown, Avatar, Tooltip, theme as antdTheme } from "antd";
import { useLocation, useNavigate } from "react-router-dom";

import {
  AiOutlineMenuFold,
  AiOutlineMenuUnfold,
  AiOutlineUser,
  AiOutlineLogout,
  AiOutlineLogin,
  AiOutlineCheckSquare,
  AiOutlineTeam,
  AiOutlineCloudServer,
  AiOutlineDatabase,
  AiOutlineMoon,
  AiOutlineSun,
  AiOutlineBook,
  AiOutlineSafetyCertificate,
  AiOutlineDashboard,
  AiOutlineControl,
  AiOutlineCode,
  AiOutlineContainer,
  AiOutlineTable,
  AiOutlineEye,
  AiOutlineEyeInvisible,
  AiOutlineDesktop,
  AiOutlineSecurityScan,
  AiOutlineScan,
  AiOutlineMonitor,
  AiOutlineQuestionCircle,
  AiOutlineInfoCircle,
  AiOutlineLineChart,
  AiOutlineApartment,
  AiOutlineBranches,
  AiOutlineKey,
  AiOutlineMedicineBox,
  AiOutlineSetting,
  AiOutlineHistory,
  AiOutlineUserAdd,
  AiOutlineUserDelete,
  AiOutlineUsergroupAdd,
  AiOutlineUsergroupDelete,
  AiOutlineUserSwitch,
  AiOutlineShareAlt,
  AiOutlineSchedule,
  AiOutlineCalendar,
  AiOutlineProject,
} from "react-icons/ai";

import { GrCertificate,
} from "react-icons/gr";

import { MdOutlineSecurity,
} from "react-icons/md";

import { SiLetsencrypt,
} from "react-icons/si";

import { APP_MENU, AppMenuEntry, AppMenuLeaf } from "./menuConfig";
import { keycloak } from "./keycloak";
import { getToken } from "./keycloak";

import { usePGBroadcastStatus } from "./realtime/usePGBroadcastStatus";
import { getPGBroadcastClient } from "./realtime/pgbroadcastSingleton";
import { usePGSubscriptionCount } from "./realtime/usePGSubscriptionCount";

const { Header, Sider, Content } = Layout;

function RealtimeDot({ status, subCount }: { status: string; subCount: number }) {
  const { token } = antdTheme.useToken();

  const { color, title } = useMemo(() => {
    if (status === "connected") {
      if (subCount > 0) {
        return {
          color: token.colorSuccess,
          title: `Realtime: Connected (${subCount} sub${subCount === 1 ? "" : "s"})`,
        };
      }
      return { color: token.colorInfo, title: "Realtime: Connected (idle)" };
    }

    switch (status) {
      case "connecting":
        return { color: token.colorWarning, title: "Realtime: Connecting…" };
      case "no_token":
        return { color: token.colorTextQuaternary, title: "Realtime: Waiting for login" };
      case "error":
        return { color: token.colorError, title: "Realtime: Error" };
      default:
        return { color: token.colorTextQuaternary, title: "Realtime: Disconnected" };
    }
  }, [status, subCount, token]);

  return (
    <Tooltip title={title} placement="bottom">
      <span
        aria-label={title}
        style={{
          width: 8,
          height: 8,
          borderRadius: 999,
          background: color,
          display: "inline-block",
          boxShadow: `0 0 0 2px ${token.colorBgContainer}`,
          flex: "0 0 auto",
        }}
      />
    </Tooltip>
  );
}

function titleForNonMenuRoute(pathname: string): string | null {
  if (pathname === "/wiki" || pathname.startsWith("/wiki/")) {
    const rest = pathname.replace(/^\/wiki\/?/, "");
    return rest ? `Wiki: ${rest}` : "Wiki";
  }
  return null;
}

function flattenLeaves(entries: AppMenuEntry[]): AppMenuLeaf[] {
  const out: AppMenuLeaf[] = [];
  for (const e of entries) {
    if (e.type === "group") out.push(...e.children);
    else out.push(e);
  }
  return out;
}

/**
 * Keep the icon map as components (not nodes) to:
 * - avoid creating elements until needed
 * - keep menu building fast and simple
 * Icons inherit `currentColor`, so they work naturally in light/dark mode.
 */
type IconComp = React.ComponentType<{ size?: number }>;
const ICONS: Record<string, IconComp> = {
  todos: AiOutlineCheckSquare,
  groups: AiOutlineTeam,
  domains: AiOutlineCloudServer,
  records: AiOutlineDatabase,
  home: AiOutlineCheckSquare,
  wiki: AiOutlineBook,
  safetycertificate: AiOutlineSafetyCertificate,
  table: AiOutlineTable,
  dashboard: AiOutlineDashboard,
  control: AiOutlineControl,
  container: AiOutlineContainer,
  code: AiOutlineCode,
  desktop: AiOutlineDesktop,
  cloudserver: AiOutlineCloudServer,
  eye: AiOutlineEye,
  eyeinvisible: AiOutlineEyeInvisible,
  securityscan: AiOutlineSecurityScan,
  scan: AiOutlineScan,
  monitor: AiOutlineMonitor,
  question: AiOutlineQuestionCircle,
  infocircle: AiOutlineInfoCircle,
  linechart: AiOutlineLineChart,
  apartment: AiOutlineApartment,
  branches: AiOutlineBranches,
  keyoutlined: AiOutlineKey,
  project: AiOutlineProject,
  medicinebox: AiOutlineMedicineBox,
  setting: AiOutlineSetting,
  history: AiOutlineHistory,
  useraddoutlined: AiOutlineUserAdd,
  userdelete: AiOutlineUserDelete,
  usergroupadd: AiOutlineUsergroupAdd,
  usergroupdelete: AiOutlineUsergroupDelete,
  userswitch: AiOutlineUserSwitch,
  sharealt: AiOutlineShareAlt,
  schedule: AiOutlineSchedule,
  calendar: AiOutlineCalendar,
  securityshield: MdOutlineSecurity,
  certificate: GrCertificate,
  siletsencrypt: SiLetsencrypt,
};

function renderMenuIcon(iconKey?: string) {
  if (!iconKey) return undefined;
  const C = ICONS[iconKey];
  return C ? <C size={16} /> : undefined;
}

interface AppLayoutProps {
  children: React.ReactNode;
  isDark?: boolean;
  onToggleTheme?: () => void;
}

export const AppLayout: React.FC<AppLayoutProps> = ({ children, isDark, onToggleTheme }) => {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const pgStatus = usePGBroadcastStatus();
  const subCount = usePGSubscriptionCount();

  const username =
    (keycloak.tokenParsed as any)?.preferred_username ||
    (keycloak.tokenParsed as any)?.name ||
    (keycloak.tokenParsed as any)?.email ||
    "";

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const {
    token: { colorBgContainer, colorBgLayout, colorText },
  } = antdTheme.useToken();

  const allLeaves = useMemo(() => flattenLeaves(APP_MENU), []);

  const findKeyByPath = useCallback(
    (pathname: string): string | undefined => {
      const matchField = (m: any) => m.path || m.href;

      const exact = allLeaves.find((m: any) => {
        const p = matchField(m);
        return p && p === pathname;
      });
      if (exact) return exact.key;

      const prefix = allLeaves.find((m: any) => {
        const p = matchField(m);
        return p && pathname.startsWith(p);
      });
      return prefix?.key;
    },
    [allLeaves],
  );

  const [selectedKey, setSelectedKey] = useState<string | undefined>(() =>
    findKeyByPath(location.pathname),
  );

  // Keep realtime client attempting to connect once token exists.
  useEffect(() => {
    const c = getPGBroadcastClient();

    if (getToken()) c.connect();

    const id = window.setInterval(() => {
      if (!getToken()) return;
      c.connect(); // idempotent
    }, 5000);

    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    const byPath = findKeyByPath(location.pathname);
    if (byPath && byPath !== selectedKey) setSelectedKey(byPath);
  }, [location.pathname, findKeyByPath, selectedKey]);

  const nonMenuTitle = useMemo(() => titleForNonMenuRoute(location.pathname), [location.pathname]);

  const activeLeaf: AppMenuLeaf | undefined = useMemo(() => {
    if (selectedKey) {
      const byKey = allLeaves.find((l) => l.key === selectedKey);
      if (byKey) return byKey;
    }
    const byPathKey = findKeyByPath(location.pathname);
    return byPathKey ? allLeaves.find((l) => l.key === byPathKey) : undefined;
  }, [allLeaves, selectedKey, findKeyByPath, location.pathname]);

  const computeOpenKeys = useCallback(() => {
    const open: string[] = [];
    for (const entry of APP_MENU) {
      if (
        entry.type === "group" &&
        entry.children.some((c) => c.path && location.pathname.startsWith(c.path))
      ) {
        open.push(entry.key);
      }
    }
    return open;
  }, [location.pathname]);

  const [openKeys, setOpenKeys] = useState<string[]>(() => computeOpenKeys());

  useEffect(() => {
    setOpenKeys(computeOpenKeys());
  }, [computeOpenKeys]);

  const userMenu = useMemo(() => {
    const whoami = keycloak.authenticated
      ? {
          key: "whoami",
          disabled: true,
          label: (
            <div style={{ lineHeight: 1.2 }}>
              <div style={{ fontSize: 12, opacity: 0.75 }}>Signed in as</div>
              <div style={{ fontWeight: 600 }}>{username || "User"}</div>
            </div>
          ),
        }
      : {
          key: "whoami",
          disabled: true,
          label: (
            <div style={{ lineHeight: 1.2 }}>
              <div style={{ fontSize: 12, opacity: 0.75 }}>Not logged in</div>
            </div>
          ),
        };

    return {
      items: [
        whoami,
        { type: "divider" as const },
        keycloak.authenticated
          ? {
              key: "logout",
              label: "Logout",
              icon: <AiOutlineLogout size={16} />,
              onClick: () => keycloak.logout(),
            }
          : {
              key: "login",
              label: "Login",
              icon: <AiOutlineLogin size={16} />,
              onClick: () => keycloak.login(),
            },
      ],
    };
  }, [username]);

  const menuItems = useMemo(() => {
    return APP_MENU.map((entry) => {
      if (entry.type === "group") {
        return {
          key: entry.key,
          label: entry.label,
          children: entry.children.map((child) => ({
            key: child.key,
            icon: renderMenuIcon(child.icon),
            label: child.label,
          })),
        };
      }
      return {
        key: entry.key,
        icon: renderMenuIcon(entry.icon),
        label: entry.label,
      };
    });
  }, []);

  return (
    <Layout style={{ minHeight: "100vh", background: colorBgLayout }}>
      <Sider collapsible collapsed={collapsed} trigger={null} width={220}>
        <div
          style={{
            height: 48,
            display: "flex",
            alignItems: "center",
            justifyContent: collapsed ? "center" : "flex-start",
            padding: collapsed ? 0 : "0 16px",
            fontWeight: 600,
            color: isDark ? "#fff" : "#000",
            whiteSpace: "nowrap",
            overflow: "hidden",
          }}
        >
          {collapsed ? "U" : "UApp"}
        </div>

        <Menu
          theme={isDark ? "dark" : "light"}
          mode="inline"
          selectedKeys={activeLeaf ? [activeLeaf.key] : []}
          openKeys={openKeys}
          onOpenChange={(keys) => setOpenKeys(keys as string[])}
          items={menuItems}
          onClick={(info) => {
            const key = info.key as string;
            setSelectedKey(key);

            const leaf = allLeaves.find((l) => l.key === key);
            if (!leaf) return;

            const href = (leaf as any).href as string | undefined;
            const path = leaf.path;

            const isExternal = !!(leaf as any).external || (!!href && /^https?:\/\//i.test(href));
            const openInNewTab = !!(leaf as any).newTab || isExternal;

            if (href) {
              if (isExternal) {
                window.open(href, openInNewTab ? "_blank" : "_self", "noopener,noreferrer");
              } else {
                navigate(href);
              }
              return;
            }

            if (path) navigate(path);
          }}
        />
      </Sider>

      <Layout>
        <Header
          style={{
            background: colorBgContainer,
            padding: "0 16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 16,
            height: 64,
          }}
        >
          {/* Left: toggle + title */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, minWidth: 0 }}>
            {collapsed ? (
              <AiOutlineMenuUnfold
                onClick={() => setCollapsed(false)}
                style={{ fontSize: 18, cursor: "pointer", flexShrink: 0 }}
              />
            ) : (
              <AiOutlineMenuFold
                onClick={() => setCollapsed(true)}
                style={{ fontSize: 18, cursor: "pointer", flexShrink: 0 }}
              />
            )}

            <div
              style={{
                fontWeight: 500,
                fontSize: 16,
                color: colorText,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                minWidth: 0,
              }}
              title={activeLeaf?.label ?? nonMenuTitle ?? ""}
            >
              {activeLeaf?.label ?? nonMenuTitle ?? ""}
            </div>
          </div>

          {/* Right: theme + avatar */}
          <div style={{ display: "flex", alignItems: "center", gap: 16, flexShrink: 0 }}>
            {onToggleTheme ? (
              isDark ? (
                <AiOutlineSun
                  onClick={onToggleTheme}
                  style={{ fontSize: 18, cursor: "pointer" }}
                  title="Switch to light theme"
                />
              ) : (
                <AiOutlineMoon
                  onClick={onToggleTheme}
                  style={{ fontSize: 18, cursor: "pointer" }}
                  title="Switch to dark theme"
                />
              )
            ) : null}

            <Dropdown menu={userMenu} placement="bottomRight" arrow>
              <div style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                <RealtimeDot status={pgStatus} subCount={subCount} />
                <Avatar size="small" icon={<AiOutlineUser size={16} />} />
              </div>
            </Dropdown>
          </div>
        </Header>

        <Content style={{ padding: 16 }}>{children}</Content>
      </Layout>
    </Layout>
  );
};

